import { Link } from 'react-router-dom'
import { Container } from './Container'
import { Phone, Mail, MapPin } from 'lucide-react'
import { useSite } from '@/context/SiteSettingsContext'
import { useFooterPropertyLinks } from '@/hooks/useFooterPropertyLinks'
import { PATHS } from '@/routes/paths'

const companyLinks = [
  { label: 'About Us', href: PATHS.about },
  { label: 'Contact Us', href: PATHS.contact },
  { label: 'Achievements', href: PATHS.achievements },
  { label: 'QBC Premium', href: PATHS.premium },
  { label: 'Cost Estimator', href: PATHS.calculator },
  { label: 'Real Estate Information', href: PATHS.realEstateBook },
  { label: 'Home Loan EMI', href: PATHS.homeLoanEmi },
  { label: 'Home Loan Eligibility', href: PATHS.homeLoanEligibility },
  { label: 'Blog', href: PATHS.blog },
  { label: 'Media', href: PATHS.media },
  { label: 'FAQs', href: PATHS.faq },
  { label: 'Magazine', href: PATHS.magazine },
  { label: 'Privacy Policy', href: PATHS.privacy },
  { label: 'Terms & Conditions', href: PATHS.terms },
]

/** Shared interactive link style — clearer default + strong hover highlight */
const footerLinkClass =
  'inline-flex text-sm font-medium text-white/85 underline-offset-4 transition-colors duration-150 hover:text-primary-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-footer'

const footerMetaLinkClass =
  'font-medium text-white/70 underline-offset-4 transition-colors duration-150 hover:text-primary-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/60'

function SocialIcon({ label }: { label: string }) {
  if (label === 'Facebook') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
        <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    )
  }
  if (label === 'Instagram') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    )
  }
  if (label === 'YouTube') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 114.126 0 2.063 2.063 0 01-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

export function Footer() {
  const site = useSite()
  const { propertyLinks, residentialLinks, commercialLandLinks } = useFooterPropertyLinks()

  const hasPopularResidential = residentialLinks.length > 0
  const hasPopularCommercial = commercialLandLinks.length > 0
  const hasAnyPopular = hasPopularResidential || hasPopularCommercial

  const socialLinks = [
    { label: 'Facebook', href: site.social.facebook },
    { label: 'Instagram', href: site.social.instagram },
    { label: 'YouTube', href: site.social.youtube },
    { label: 'LinkedIn', href: site.social.linkedin },
  ].filter((item) => item.href)

  return (
    <footer className="bg-footer text-neutral-300 max-md:pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))]">
      <Container className="py-8 md:py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="max-w-sm text-sm leading-relaxed text-white/70">
              {site.legalName} — your trusted property partner in Gujarat. Verified listings, expert support, and seamless discovery for buyers, tenants & owners.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {socialLinks.map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white/90 transition-all hover:border-primary-400/50 hover:bg-primary-500/25 hover:text-primary-200"
                >
                  <SocialIcon label={label} />
                </a>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={site.apps.playStore}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get QBC app on Google Play"
                className="transition-opacity hover:opacity-90"
              >
                <img
                  src={site.apps.playStoreBadge}
                  alt="Get it on Google Play"
                  className="h-10 w-auto"
                  loading="lazy"
                />
              </a>
              <a
                href={site.apps.appStore}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download QBC app on the App Store"
                className="transition-opacity hover:opacity-90"
              >
                <img
                  src={site.apps.appStoreBadge}
                  alt="Download on the App Store"
                  className="h-10 w-auto"
                  loading="lazy"
                />
              </a>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <a
                href={`tel:${site.phoneTel}`}
                className="group flex items-center gap-3 font-medium text-white/90 transition-colors hover:text-primary-300"
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 leading-none transition-colors group-hover:bg-primary-500/30">
                  <Phone className="h-4 w-4" />
                </span>
                {site.phone}
              </a>
              <a
                href={`mailto:${site.email}`}
                className="group flex items-center gap-3 font-medium text-white/90 transition-colors hover:text-primary-300"
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 leading-none transition-colors group-hover:bg-primary-500/30">
                  <Mail className="h-4 w-4" />
                </span>
                {site.email}
              </a>
              <p className="flex items-center gap-3 text-white/75">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 leading-none">
                  <MapPin className="h-4 w-4 text-white/90" aria-hidden />
                </span>
                {site.address}
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-white">Company</h4>
            <ul className="space-y-2.5">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-white">Properties</h4>
            <ul className="space-y-2.5">
              {propertyLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {hasAnyPopular ? (
            <div className="lg:col-span-4">
              <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-white">
                Popular Searches
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {hasPopularResidential ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-primary-300">Residential</p>
                    <ul className="space-y-1.5">
                      {residentialLinks.map((link) => (
                        <li key={link.href}>
                          <Link to={link.href} className={footerLinkClass}>
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {hasPopularCommercial ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-primary-300">Commercial & Land</p>
                    <ul className="space-y-1.5">
                      {commercialLandLinks.map((link) => (
                        <li key={link.href}>
                          <Link to={link.href} className={footerLinkClass}>
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-center text-xs text-white/45 md:text-left">
            © {new Date().getFullYear()} {site.legalName}. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-xs">
            <Link to={PATHS.privacy} className={footerMetaLinkClass}>
              Privacy
            </Link>
            <Link to={PATHS.terms} className={footerMetaLinkClass}>
              Terms
            </Link>
            <a
              href={`https://wa.me/${site.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className={footerMetaLinkClass}
            >
              WhatsApp Support
            </a>
          </div>
        </div>
      </Container>
    </footer>
  )
}
